import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useFamilyChild } from './FamilyShell';
import { parentApi } from './parentApi';

export default function FamilyThread() {
  const { threadId } = useParams();
  const { child } = useFamilyChild();
  const qc = useQueryClient();
  const q = useQuery(
    ['familyThread', child.studentId, threadId],
    async () => (await parentApi.thread(child.studentId, threadId)).data?.data,
    { enabled: !!child.studentId && !!threadId }
  );
  const [body, setBody] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const t = q.data;

  const send = async () => {
    setErr('');
    if (!body.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('body', body);
      await parentApi.replyMessage(child.studentId, threadId, fd);
      setBody('');
      qc.invalidateQueries(['familyThread', child.studentId, threadId]);
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not send.');
    } finally {
      setSaving(false);
    }
  };

  if (q.isLoading) return <CircularProgress />;
  if (!t) return <Typography color="text.secondary">Conversation not found.</Typography>;

  return (
    <Box>
      <Typography variant="h6">{t.staffName} · {t.roleLabel}</Typography>
      <Typography variant="caption" color="text.secondary">About {child.firstName}</Typography>
      {t.familyNote && <Alert sx={{ mt: 1 }} severity="info">{t.familyNote}</Alert>}
      <Box sx={{ my: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {(t.messages || []).map((m) => (
          <Box
            key={m.messageId}
            sx={{
              alignSelf: m.senderKind === 'parent' ? 'flex-end' : 'flex-start',
              bgcolor: m.senderKind === 'parent' ? 'rgba(224,122,95,0.16)' : 'rgba(27,42,74,0.06)',
              borderRadius: 2, px: 1.5, py: 1, maxWidth: '85%',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {m.senderKind === 'parent' ? 'You' : t.staffName}
            </Typography>
            <Typography variant="body2">{m.body}</Typography>
          </Box>
        ))}
      </Box>
      {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}
      {t.replyByLabel ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Reply by {t.replyByLabel}
        </Typography>
      ) : null}
      <TextField fullWidth multiline minRows={2} placeholder="Write a message" value={body} onChange={(e) => setBody(e.target.value)} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <Button variant="contained" onClick={send} disabled={saving}>Send about {child.firstName}</Button>
      </Box>
    </Box>
  );
}
